import type {
  AlteracaoDeLancamento,
  AnexoDoLancamento,
  BeneficiarioDaFolha,
  CancelamentoDeLancamento,
  CategoriaDoPlano,
  ConfirmacaoDeAnexo,
  ConsultaDoExtrato,
  DetalheDoLancamento,
  EnvioDeAnexo,
  EstornoDeLancamento,
  LancamentoCriado,
  LancamentoNoExtrato,
  NovoLancamento,
  PaginaDaFolha,
  PaginaDoExtrato,
  PedidoDeEnvioDeAnexo,
  RealizacaoDeLancamento,
  ResultadoDeCancelamento,
  ResultadoDeEstorno,
  ResumoFinanceiro,
  UrlDeAnexo,
} from '@arena/shared/schemas';
import { api } from './cliente.js';

/**
 * As chamadas do razão — `#v-fin` e `#m-lancamento`.
 *
 * A superfície é a do `FinanceiroController` inteiro, um método por
 * rota, mesmo que esta fase só use uma parte dela — o mesmo padrão de
 * `agendaApi`. Os tipos são os de `@arena/shared/schemas`, os mesmos
 * que o backend usa para validar e responder.
 *
 * `extrato()` e `folha()` recebem um objeto de opções TODO opcional —
 * e não o tipo inferido de `zConsultaDoExtrato`/`zConsultaDaFolha`,
 * porque o `.default(25)` do zod faz `limite` OBRIGATÓRIO no tipo de
 * SAÍDA do schema. Para montar a query string aqui, cada campo precisa
 * poder faltar — o mesmo espírito de `agendaApi.grade(data?: string)`.
 *
 * Ver: Arena_backend/Docs/plano/11-financeiro-razao.md
 */
export const financeiroApi = {
  /** Sem mês, o servidor responde o mês corrente — no fuso da arena. */
  resumo(mes?: string): Promise<ResumoFinanceiro> {
    return api<ResumoFinanceiro>(
      mes ? `/financeiro/resumo?mes=${encodeURIComponent(mes)}` : '/financeiro/resumo',
    );
  },

  /** O plano de contas que o perfil alcança — a Gestão não recebe "Professores". */
  categorias(): Promise<CategoriaDoPlano[]> {
    return api<CategoriaDoPlano[]>('/financeiro/categorias');
  },

  /** O mês, os filtros (tipo, centro, categoria, origem) e a posição na lista: tudo opcional. */
  extrato(consulta: Partial<ConsultaDoExtrato> = {}): Promise<PaginaDoExtrato> {
    return api<PaginaDoExtrato>(`/financeiro/lancamentos${queryString(consulta)}`);
  },

  detalhe(id: string): Promise<DetalheDoLancamento> {
    return api<DetalheDoLancamento>(`/financeiro/lancamentos/${id}`);
  },

  criar(dados: NovoLancamento): Promise<LancamentoCriado> {
    return api<LancamentoCriado>('/financeiro/lancamentos', { metodo: 'POST', corpo: dados });
  },

  alterar(id: string, dados: AlteracaoDeLancamento): Promise<LancamentoNoExtrato> {
    return api<LancamentoNoExtrato>(`/financeiro/lancamentos/${id}`, {
      metodo: 'PATCH',
      corpo: dados,
    });
  },

  /** `Previsto` → `Realizado`. */
  realizar(id: string, dados: RealizacaoDeLancamento): Promise<LancamentoNoExtrato> {
    return api<LancamentoNoExtrato>(`/financeiro/lancamentos/${id}/realizar`, {
      metodo: 'POST',
      corpo: dados,
    });
  },

  /** Lançamento contrário — nunca `DELETE`. */
  estornar(id: string, dados: EstornoDeLancamento): Promise<ResultadoDeEstorno> {
    return api<ResultadoDeEstorno>(`/financeiro/lancamentos/${id}/estornar`, {
      metodo: 'POST',
      corpo: dados,
    });
  },

  /** Só a previsão se cancela — o fato se estorna. */
  cancelar(id: string, dados: CancelamentoDeLancamento): Promise<ResultadoDeCancelamento> {
    return api<ResultadoDeCancelamento>(`/financeiro/lancamentos/${id}/cancelar`, {
      metodo: 'POST',
      corpo: dados,
    });
  },

  /** Quem pode entrar no detalhamento da folha — exige a permissão `folha`. */
  beneficiariosDaFolha(): Promise<BeneficiarioDaFolha[]> {
    return api<BeneficiarioDaFolha[]>('/financeiro/folha/beneficiarios');
  },

  /** "Próprio pagamento" — a tela do Professor. */
  folha(consulta: { cursor?: string; limite?: number } = {}): Promise<PaginaDaFolha> {
    return api<PaginaDaFolha>(`/financeiro/folha${queryString(consulta)}`);
  },

  /** Primeiro passo do anexo: para onde e como enviar. Nada é gravado ainda. */
  pedirEnvioDeAnexo(lancamentoId: string, dados: PedidoDeEnvioDeAnexo): Promise<EnvioDeAnexo> {
    return api<EnvioDeAnexo>(`/financeiro/lancamentos/${lancamentoId}/anexos/envio`, {
      metodo: 'POST',
      corpo: dados,
    });
  },

  /** Segundo passo: "terminei de enviar". O servidor confere o objeto antes de gravar. */
  confirmarAnexo(lancamentoId: string, dados: ConfirmacaoDeAnexo): Promise<AnexoDoLancamento> {
    return api<AnexoDoLancamento>(`/financeiro/lancamentos/${lancamentoId}/anexos/confirmar`, {
      metodo: 'POST',
      corpo: dados,
    });
  },

  /** URL assinada de leitura — expira em minutos. */
  urlDoAnexo(anexoId: string): Promise<UrlDeAnexo> {
    return api<UrlDeAnexo>(`/financeiro/anexos/${anexoId}/url`);
  },

  excluirAnexo(anexoId: string): Promise<void> {
    return api<void>(`/financeiro/anexos/${anexoId}`, { metodo: 'DELETE' });
  },
};

function queryString(parametros: Record<string, string | number | undefined>): string {
  const busca = new URLSearchParams();
  for (const [chave, valor] of Object.entries(parametros)) {
    if (valor !== undefined) busca.set(chave, String(valor));
  }
  const texto = busca.toString();
  return texto ? `?${texto}` : '';
}
